import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon, RadarIcon, SaveIcon } from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import { Button } from '@/shared/ui/buttons/Button';
import { Input } from '@/shared/ui/inputs/Input';
import { Spinner } from '@/shared/ui/feedback/Spinner';
import { EmptyState } from '@/shared/ui/feedback/EmptyState';
import { ErrorState } from '@/shared/ui/feedback/ErrorState';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useAuthState } from '@/entities/auth';
import {
  apiTrackedDelete,
  apiTrackedSave,
  useTrackLookup,
  useTrackedList,
} from '@/entities/tracking';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { extractErrorMessage } from '@/shared/api/errors';
import type { ApiTrackResult } from '@/shared/types/api/tracking';
import { CLIENT_STATUS_VALUES, type ClientStatus } from '@/shared/types/api/parcels';
import { TrackStateChip } from './TrackStateChip';
import { SavedRow } from './SavedRow';

const CLIENT_STATUS_SET = new Set<string>(CLIENT_STATUS_VALUES);

const isClientStatusCode = (value: string): value is ClientStatus =>
  CLIENT_STATUS_SET.has(value);

const INVALIDATE = [queryKeys.tracking.all, queryKeys.tracking.saved];

const TREK_ID_PATTERN = /^[A-Za-z0-9]+$/;
const TREK_ID_MIN = 5;
const TREK_ID_MAX = 255;

/**
 * Strip whitespace anywhere as a paste-nicety AND uppercase everything (owner
 * spec: all trek IDs are stored uppercase, so normalising here keeps the input
 * and the validation regex aligned regardless of caps-lock state).
 */
const normalizeTrekId = (value: string): string => value.replace(/\s+/g, '').toUpperCase();

const isValidTrekId = (value: string): boolean =>
  value.length >= TREK_ID_MIN && value.length <= TREK_ID_MAX && TREK_ID_PATTERN.test(value);

export const WidgetsTrackMain = () => {
  const { t } = useTranslation('tracking');
  const { t: tCommon } = useTranslation('common');
  const { t: tClientStatus } = useTranslation('clientStatus');
  const toast = useToast();
  const { isAuthenticated } = useAuthState();
  const [searchParams] = useSearchParams();
  const inputRef = useRef<HTMLInputElement>(null);
  // Holds the trek_id currently pending confirmation in the remove dialog.
  const [removing, setRemoving] = useState<{ trekId: string } | null>(null);

  // Optional prefill from ?trek_id= on first mount only; we never write back to the URL.
  const initialFromQuery = useMemo(
    () => normalizeTrekId(searchParams.get('trek_id') ?? ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [inputValue, setInputValue] = useState<string>(initialFromQuery);
  const [touched, setTouched] = useState<boolean>(false);
  // For UNAUTH users only: holds the trek_id whose lookup card is currently shown.
  const [submitted, setSubmitted] = useState<string>(() =>
    !isAuthenticated && initialFromQuery && isValidTrekId(initialFromQuery) ? initialFromQuery : '',
  );

  const lookup = useTrackLookup(submitted, !isAuthenticated && submitted.length > 0);
  // Always enabled for auth users so a refresh restores the saved list.
  const savedList = useTrackedList(isAuthenticated);

  // Focus the input on first mount so the user can start typing immediately.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmed = inputValue; // we already strip whitespace on change
  const hasInput = trimmed.length > 0;
  const valid = isValidTrekId(trimmed);
  // Show the inline error only once the user has interacted (typed/blurred/submitted)
  // AND the current value is non-empty but invalid. A pristine empty field shows no error.
  const errorText = touched && hasInput && !valid ? t('errors.invalidFormat') : undefined;

  const focusInput = (): void => {
    // next microtask so React commits the state change first
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const save = useMutate({
    api: (trekId: string) => apiTrackedSave({ trek_id: trekId }),
    invalidateKeys: INVALIDATE,
    onSuccess: () => {
      toast.success(t('toast.saved'));
      setInputValue('');
      setTouched(false);
      focusInput();
    },
    onError: (_e, message) => toast.error(message),
  });

  const remove = useMutate({
    api: (trekId: string) => apiTrackedDelete(trekId),
    invalidateKeys: INVALIDATE,
    onSuccess: () => {
      toast.success(t('toast.removed'));
      setRemoving(null);
    },
    onError: (_e, message) => toast.error(message),
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setTouched(true);
    if (!valid) return;
    if (isAuthenticated) {
      // DB-first: save the trek_id. NEVER push ?trek_id= into the URL.
      save.mutate(trimmed);
    } else {
      // Public lookup flow (unchanged behaviour).
      setSubmitted(trimmed);
    }
  };

  const result: ApiTrackResult | undefined = lookup.data;
  const savedItems = savedList.data ?? [];
  const busy = save.isPending || remove.isPending;

  const stateHint = (state: ApiTrackResult['state'], partyStatus?: string): string => {
    if (state === 'in_party') {
      if (!partyStatus) return t('hint.in_party');
      // Same logic as the chip: translate the enum code if known, otherwise
      // fall back to the raw backend string.
      return isClientStatusCode(partyStatus) ? tClientStatus(partyStatus) : partyStatus;
    }
    return t(`hint.${state}`);
  };

  const submitLabel = isAuthenticated ? t('submitSave') : t('submit');
  const submitDisabled = !valid || busy;
  const submitLoading = isAuthenticated ? save.isPending : lookup.isFetching && submitted.length > 0;

  return (
    <>
      <PageHeader title={t('title')} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-end"
        >
          <div className="min-w-0 flex-1">
            <Input
              ref={inputRef}
              label={t('inputLabel')}
              placeholder={t('inputPlaceholder')}
              value={inputValue}
              onChange={(e) => {
                const v = normalizeTrekId(e.target.value);
                e.target.value = v;
                setInputValue(v);
                if (!touched) setTouched(true);
              }}
              onBlur={() => setTouched(true)}
              prefix={<SearchIcon className="h-4 w-4" />}
              autoComplete="off"
              inputMode="text"
              maxLength={TREK_ID_MAX}
              error={errorText}
            />
          </div>
          <Button
            type="submit"
            block
            leftIcon={isAuthenticated ? <SaveIcon className="h-4 w-4" /> : undefined}
            loading={submitLoading}
            className="sm:w-auto sm:min-w-32"
            disabled={submitDisabled}
          >
            {submitLabel}
          </Button>
        </form>

        {isAuthenticated && (
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-ink-soft">{t('savedListTitle')}</h2>
            {savedList.isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : savedList.isError ? (
              <ErrorState
                message={extractErrorMessage(savedList.error, t('loadError'))}
                onRetry={() => void savedList.refetch()}
              />
            ) : savedItems.length === 0 ? (
              <EmptyState
                icon={<RadarIcon className="h-10 w-10" aria-hidden />}
                title={t('savedEmpty')}
              />
            ) : (
              <div className="flex flex-col gap-2">
                {savedItems.map((item) => (
                  <SavedRow
                    key={item.id}
                    item={item}
                    busy={busy}
                    onRemove={(trekId) => setRemoving({ trekId })}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {!isAuthenticated && submitted.length > 0 && (
          <>
            {lookup.isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner size="lg" />
              </div>
            ) : lookup.isError ? (
              <ErrorState
                message={extractErrorMessage(lookup.error, t('loadError'))}
                onRetry={() => void lookup.refetch()}
              />
            ) : result ? (
              <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wide text-ink-muted">
                      {t('resultLabel')}
                    </p>
                    <p className="mt-0.5 truncate text-base font-semibold text-ink">
                      {result.trek_id}
                    </p>
                  </div>
                  <TrackStateChip
                    state={result.state}
                    partyStatus={result.party_status}
                  />
                </div>
                <p className="text-sm text-ink-muted">
                  {stateHint(result.state, result.party_status)}
                </p>
              </div>
            ) : null}
          </>
        )}
      </div>
      <ConfirmDialog
        open={removing !== null}
        title={t('confirmRemoveTitle')}
        description={
          removing ? t('confirmRemoveDescription', { trekId: removing.trekId }) : undefined
        }
        confirmLabel={tCommon('delete')}
        cancelLabel={tCommon('cancel')}
        confirmVariant="danger"
        loading={remove.isPending}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
        onConfirm={() => {
          if (removing) remove.mutate(removing.trekId);
        }}
      />
    </>
  );
};
